import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Field } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { action } from './api.ts';
import { money } from './format.ts';
import { useSession } from './session.tsx';
import { useSave } from '../screens/manage/common.tsx';

/**
 * The budget line a purchase order or bill is paid from (institutions with the Budget module).
 * On a new or draft document the line is picked and committed when it is saved; on a sent order or
 * a bill it can be set or moved at once. Nothing shows without an approved budget for the year.
 */

export type BudgetDocument = 'purchase.order' | 'purchase.bill' | 'procurement.contract';
interface ForDocument {
  year: { id: string; year: number; state: string; control: 'off' | 'warn' | 'strict' } | null;
  commitment: { id: string; name: string; line: string; amount: number; spent: number; state: string } | null;
  line: { id: string; code: string; name: string; available: number } | null;
  lines: { id: string; code: string; name: string; group: string | null; available: number }[];
  can: boolean;
}

export function useBudgetFor(document: BudgetDocument, record: string | null | undefined) {
  const { can } = useSession();
  const on = can('budget.view');
  const [data, setData] = useState<ForDocument | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!on) return;
    let live = true;
    action<ForDocument>('budget.for_document', { document, record: record ?? null }).then(
      (r) => live && setData(r),
      () => live && setData(null),
    );
    return () => {
      live = false;
    };
  }, [on, document, record, tick]);
  const usable = !!data?.year && data.year.state === 'approved' && data.lines.length > 0;
  return { data: usable ? data : null, reload: () => setTick((n) => n + 1) };
}

/** Commits a saved document to the chosen line (does nothing without a line). */
export async function commitToLine(document: BudgetDocument, record: string, line: string | null | undefined) {
  if (line) await action('budget.commit', { line, document, record });
}

/** A select of the year's lines, for a document being written. */
export function BudgetLineSelect({ budget, value, onChange, amount }: { budget: ForDocument; value: string; onChange: (v: string) => void; amount?: number }) {
  const strict = budget.year!.control === 'strict';
  const chosen = budget.lines.find((l) => l.id === value);
  const over = chosen && amount !== undefined && amount > chosen.available;
  return (
    <Field
      label={t('bud.paid_from', { year: budget.year!.year })}
      hint={chosen ? t('bud.available_now', { amount: money(chosen.available) }) : strict ? t('bud.line_required') : t('bud.line_optional')}
      error={over ? (strict ? t('bud.over_strict', { amount: money(chosen.available) }) : t('bud.over_warn', { amount: money(chosen.available) })) : null}
    >
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)} disabled={!budget.can}>
        <option value="">{t('bud.no_line')}</option>
        {budget.lines.map((l) => (
          <option key={l.id} value={l.id}>
            {l.code} · {l.name}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** On a saved document: the line it is paid from, with a way to set or move it. */
export function BudgetLineBox({ document, record, editable, onChange }: { document: BudgetDocument; record: string; editable: boolean; onChange?: () => Promise<void> | void }) {
  const { data, reload } = useBudgetFor(document, record);
  const { busy, run } = useSave();
  const [editing, setEditing] = useState(false);
  if (!data) return null;
  const c = data.commitment && data.commitment.state !== 'cancelled' ? data.commitment : null;
  const canEdit = editable && data.can && (!c || document === 'purchase.bill' || c.spent === 0);
  const pick = (line: string) =>
    void run(async () => {
      if (!line) return;
      await commitToLine(document, record, line);
      setEditing(false);
      reload();
      await onChange?.();
    }, t('bud.line_set'));
  return (
    <div className={`bud-doc${c ? '' : ' none'}`}>
      <Icon name="bank" size={16} />
      {editing ? (
        <select className="input" defaultValue={c?.line ?? ''} onChange={(e) => pick(e.target.value)} disabled={busy} aria-label={t('bud.paid_from', { year: data.year!.year })} autoFocus>
          <option value="" disabled>
            {t('bud.pick_line')}
          </option>
          {data.lines.map((l) => (
            <option key={l.id} value={l.id}>
              {l.code} · {l.name} · {money(l.available)}
            </option>
          ))}
        </select>
      ) : (
        <span>
          {c && data.line ? (
            <>
              <span className="muted">{t('bud.paid_from', { year: data.year!.year })}: </span>
              <strong>
                {data.line.code} · {data.line.name}
              </strong>
              <span className="muted"> · {c.name}</span>
            </>
          ) : (
            <span className="muted">{data.year!.control === 'strict' ? t('bud.no_line_strict') : t('bud.no_line_yet')}</span>
          )}
        </span>
      )}
      {canEdit && !editing ? (
        <button type="button" className="link-button" onClick={() => setEditing(true)}>
          {c ? t('bud.move_line') : t('bud.pick_line')}
        </button>
      ) : null}
    </div>
  );
}
